import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { datasetsSelector } from 'Redux/thunks/datasets.thunks';
import { pipesSelector, searchIsActiveSelector, systemsSelector } from 'Redux/selectors';
import { isGlobal } from 'Internals/pipes';
import withLocalStorage from 'Common/withLocalStorage/withLocalStorage';
import { PipesAPI } from '../api/pipes.api';
import { decoratePipe } from '../redux-store/reducers/decoratePipe';
import { getInternalDataEntities } from '../utils/commonDataEntities.utils';
import { getFromLocalStorage, setIntoLocalStorage } from 'Internals/local-storage';
import { StorageKeys } from '../constants/storageKeys';

import { CommandPalette } from '../containers/CommandPalette.container';
import { getDatasetLink } from '../components/common/Links/DatasetLink';
import { commandPaletteRenderer } from '../components/command-palette/CommandPaletteRenderers.view';

import { SESAM_RECENT_SEARCHES_STORAGE_KEY } from '../constants/commonConstants';

const MAX_MATCHES = 25;
const EXTERNAL_PIPES_CACHE_KEY = 'external';
const INTERNAL_PIPES_CACHE_KEY = 'internal';
const pipesCache = {};
const pipesCacheInFlight = {};

const hasInternalPipes = (pipes) => getInternalDataEntities(pipes).length > 0;

const getPipesCacheKey = (subId, includeInternalObjects) =>
  `${subId}-${includeInternalObjects ? INTERNAL_PIPES_CACHE_KEY : EXTERNAL_PIPES_CACHE_KEY}`;

const getCachedPipes = (subId, includeInternalObjects) =>
  pipesCache[getPipesCacheKey(subId, includeInternalObjects)];

const setCachedPipes = (subId, includeInternalObjects, pipes) => {
  pipesCache[getPipesCacheKey(subId, includeInternalObjects)] = pipes;
};

// Wouldn't need these if React accepted `Symbol`s as keys…
const makeDatasetKey = (id) => `dataset/${id}`;
const makePipeKey = (id) => `pipe/${id}`;
const makeSystemKey = (id) => `system/${id}`;

export const DatahubObjectsPaletteId = 'datahubObjects';

const matchSubscriptionObj = (obj, regExp) => regExp.test(obj._id) || regExp.test(obj.name);

class DatahubObjectsPalette extends React.Component {
  constructor(props) {
    super(props);

    this.isPaletteMounted = false;

    this.syncPipesCacheFromRedux = (pipes) => {
      if (!Array.isArray(pipes) || pipes.length === 0) {
        return;
      }

      const includeInternalObjects = hasInternalPipes(pipes);
      setCachedPipes(this.props.subId, includeInternalObjects, pipes);
    };

    this.loadPipes = (includeInternalObjects) => {
      const cachedPipes = getCachedPipes(this.props.subId, includeInternalObjects);

      if (cachedPipes) {
        return Promise.resolve(cachedPipes);
      }

      const key = getPipesCacheKey(this.props.subId, includeInternalObjects);

      if (pipesCacheInFlight[key]) {
        return pipesCacheInFlight[key];
      }

      const request = PipesAPI.getAll(
        {
          subUrl: this.props.subUrl,
          token: this.props.token,
        },
        includeInternalObjects
      )
        .then((pipes) => pipes.map((pipe) => decoratePipe(pipe)))
        .then((pipes) => {
          setCachedPipes(this.props.subId, includeInternalObjects, pipes);

          return pipes;
        })
        .finally(() => {
          delete pipesCacheInFlight[key];
        });

      pipesCacheInFlight[key] = request;

      return request;
    };

    this.saveSearchToLS = (query) => {
      let updatedRecentSearches = this.state.recentSearches.slice();
      const duplicateIndex = this.state.recentSearches.findIndex(
        (s) => s.id === query.id && s.type === query.type
      );
      if (duplicateIndex < 0) {
        updatedRecentSearches.unshift(query);
      } else {
        updatedRecentSearches.splice(duplicateIndex, 1);
        updatedRecentSearches.unshift(query);
      }
      if (updatedRecentSearches.length > MAX_MATCHES) {
        updatedRecentSearches = updatedRecentSearches.slice(MAX_MATCHES);
      }
      this.props.saveToLocalStorage('recentSearches', updatedRecentSearches);
    };

    this.getRecentResults = (recentSearches) => {
      const results = recentSearches.map((s) => {
        switch (s.type) {
          case 'global': {
            const pipe = this.props.pipesDict[s.id];

            if (pipe) {
              return this.makeGlobalResult(pipe._id, pipe.name);
            } else {
              return null;
            }
          }
          case 'pipe': {
            const pipe = this.props.pipesDict[s.id];

            if (pipe) {
              return this.makePipeResult(pipe._id, pipe.name);
            } else {
              return null;
            }
          }
          case 'dataset': {
            const dataset = this.props.datasetsDict[s.id];

            if (dataset) {
              return this.makeDatasetResult(dataset._id, dataset.name);
            } else {
              return null;
            }
          }
          case 'system': {
            const system = this.props.systemsDict[s.id];

            if (system) {
              return this.makeSystemResult(system._id, system.name);
            } else {
              return null;
            }
          }
          default: {
            return null;
          }
        }
      });

      return results.filter(Boolean);
    };

    this.onDatasetActivate = (datasetId) => {
      this.saveSearchToLS({ type: 'dataset', id: datasetId });
      this.navigateToDataset(dataset);
    };

    this.navigateToDataset = (datasetId) => {
      const path = getDatasetLink(datasetId, this.props.datasets, this.props.upstreams);
      this.props.router.push(`/subscription/${this.props.subId}/${path}`);
    };

    this.onPipeActivate = (pipeId) => {
      this.saveSearchToLS({ type: 'pipe', id: pipeId });
      this.navigateToPipe(pipeId);
    };

    this.onGlobalPipeActivate = (pipeId) => {
      this.saveSearchToLS({ type: 'global', id: pipeId });
      this.navigateToPipe(pipeId);
    };

    this.navigateToPipe = (pipeId) => {
      this.props.router.push(
        `/subscription/${this.props.subId}/pipes/pipe/${encodeURIComponent(pipeId)}/`
      );
    };

    this.onSystemActivate = (systemId) => {
      this.saveSearchToLS({ type: 'system', id: systemId });
      this.navigateToSystem(systemId);
    };

    this.navigateToSystem = (systemId) => {
      this.props.router.push(
        `/subscription/${this.props.subId}/systems/system/${encodeURIComponent(systemId)}/`
      );
    };

    this.makeGlobalResult = (pipeId, pipeName) => ({
      item: { id: pipeId, name: pipeName },
      keyMaker: makePipeKey,
      matcher: matchSubscriptionObj,
      onActivate: this.onGlobalPipeActivate,
      renderer: commandPaletteRenderer,
      type: 'global',
    });

    this.makePipeResult = (pipeId, pipeName) => ({
      item: { id: pipeId, name: pipeName },
      keyMaker: makePipeKey,
      matcher: matchSubscriptionObj,
      onActivate: this.onPipeActivate,
      renderer: commandPaletteRenderer,
      type: 'pipe',
    });

    this.makeDatasetResult = (datasetId, datasetName) => ({
      item: { id: datasetId, name: datasetName },
      keyMaker: makeDatasetKey,
      matcher: matchSubscriptionObj,
      onActivate: this.onDatasetActivate,
      renderer: commandPaletteRenderer,
      type: 'dataset',
    });

    this.makeSystemResult = (systemId, systemName) => ({
      item: { id: systemId, name: systemName },
      keyMaker: makeSystemKey,
      matcher: matchSubscriptionObj,
      onActivate: this.onSystemActivate,
      renderer: commandPaletteRenderer,
      type: 'system',
    });

    this.makeHaystack = this.makeHaystack.bind(this);
    this.syncPipesCacheFromRedux(this.props.pipes);

    const initialIncludeInternalObjects = getFromLocalStorage(
      StorageKeys.commandPalettePreferences,
      'includeInternalObjects',
      false
    );
    const initialPipes = getCachedPipes(this.props.subId, initialIncludeInternalObjects) || [];

    this.state = {
      includeInternalObjects: initialIncludeInternalObjects,
      palettePipes: initialPipes,
      recentSearches: [],
    };
  }

  *makeHaystack(prefix) {
    const pipes = this.state.palettePipes;

    if (prefix === 'global' || prefix === '') {
      const globals = pipes.filter(isGlobal);
      for (let i = 0; i < globals.length; ++i) {
        const globalPipe = globals[i];

        yield this.makeGlobalResult(globalPipe._id, globalPipe.name);
      }
    }

    if (prefix === 'pipe' || prefix === '') {
      const nonGlobals = pipes.filter((pipe) => !isGlobal(pipe));
      for (let i = 0; i < nonGlobals.length; ++i) {
        const noneGlobal = nonGlobals[i];

        yield this.makePipeResult(noneGlobal._id, noneGlobal.name);
      }
    }

    if (prefix === 'system' || prefix === '') {
      for (let i = 0; i < this.props.systems.length; ++i) {
        const system = this.props.systems[i];

        yield this.makeSystemResult(system._id, system.name);
      }
    }
  }

  componentDidMount() {
    this.isPaletteMounted = true;

    const recentSearches = this.props.loadFromLocalStorage('recentSearches');
    this.setState({ recentSearches });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.pipes !== this.props.pipes) {
      this.syncPipesCacheFromRedux(this.props.pipes);

      const includeInternalObjects = hasInternalPipes(this.props.pipes);

      if (includeInternalObjects === this.state.includeInternalObjects) {
        this.setState({ palettePipes: this.props.pipes });
      }
    }
  }

  componentWillUnmount() {
    this.isPaletteMounted = false;
  }

  onIncludeInternalObjectsChange = (includeInternalObjects) => {
    setIntoLocalStorage(
      StorageKeys.commandPalettePreferences,
      'includeInternalObjects',
      includeInternalObjects
    );
    this.setState({ includeInternalObjects });

    this.loadPipes(includeInternalObjects).then((pipes) => {
      if (!this.isPaletteMounted) {
        return;
      }

      if (this.state.includeInternalObjects === includeInternalObjects) {
        this.setState({ palettePipes: pipes });
      }
    });
  };

  render() {
    return (
      <CommandPalette
        haystackGenerator={this.makeHaystack}
        subId={this.props.subId}
        onRequestClose={this.props.onRequestClose}
        recentResults={this.getRecentResults(this.state.recentSearches)}
        haystackVersion={this.state.palettePipes}
        initialIncludeInternalObjects={this.state.includeInternalObjects}
        onIncludeInternalObjectsChange={this.onIncludeInternalObjectsChange}
        hasSearch={this.props.hasSearch}
        isItestMode={this.props.isItestMode}
        darkModeActive={this.props.darkModeActive}
      />
    );
  }
}

DatahubObjectsPalette.propTypes = {
  /** All the datasets */
  datasets: PropTypes.arrayOf(PropTypes.object).isRequired,

  /** All datasets as dict */
  datasetsDict: PropTypes.shape({}).isRequired,

  /** All the pipes as array */
  pipes: PropTypes.arrayOf(PropTypes.object).isRequired,

  /** All pipes as dict */
  pipesDict: PropTypes.shape({}),

  /** All the systems */
  systems: PropTypes.arrayOf(PropTypes.object).isRequired,

  /** All systems as dict */
  systemsDict: PropTypes.shape({}),

  upstreams: PropTypes.shape({}),

  /** Handler for when the user requests closing the palette (e.g. by pressing
      `Esc`) — this should update the parent component's state so as to unmount
      this component */
  onRequestClose: PropTypes.func.isRequired,

  /** ID of the subscription */
  subId: PropTypes.string.isRequired,

  /** {Router} */
  router: PropTypes.object.isRequired,

  loadFromLocalStorage: PropTypes.func.isRequired,
  saveToLocalStorage: PropTypes.func.isRequired,
  subUrl: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  hasSearch: PropTypes.bool.isRequired,
  isItestMode: PropTypes.bool,
};

function mapStateToProps(state) {
  return {
    datasets: datasetsSelector(state),
    datasetsDict: state.datasets,
    pipes: pipesSelector(state),
    pipesDict: state.pipes,
    subId: state.subscription.id,
    subUrl: state.subscription.url,
    systems: systemsSelector(state),
    systemsDict: state.systems,
    token: state.subscription.token,
    hasSearch: searchIsActiveSelector(state),
    upstreams: state.upstreams,
    isItestMode: state.globals.status.is_itest_mode,
    darkModeActive: state.theme.dark,
  };
}
const mapPropsToLS = {
  recentSearches: {
    path: ['recentSearches'],
    defaultValue: [],
    bySubscription: true,
  },
};

const Composed = compose(
  withLocalStorage(SESAM_RECENT_SEARCHES_STORAGE_KEY, mapPropsToLS),
  withRouter,
  connect(mapStateToProps)
)(DatahubObjectsPalette);

export { Composed as DatahubObjectsPalette };
